import React, { Component } from 'react';
import ReactMarkdown from 'react-markdown';
import AdminWrapper from './AdminWrapper';
import { adminService } from '../../_services/admin.service';
import toast from '../shared/Toast';

/**
 * Admin → Policies.
 *
 * Versioned content management for the three legal documents. Publishing is
 * APPEND-ONLY: every publish mints a new numbered version with its own date;
 * old versions are permanently viewable and never editable — that immutability
 * is what makes "user accepted v4" mean something.
 */

const POLICY_TYPES = [
  { key: 'partner_terms', label: 'Partner Terms' },
  { key: 'beu_pay_terms', label: 'Beu Pay Terms' },
  { key: 'client_terms', label: 'Client Terms' },
  { key: 'privacy_policy', label: 'Privacy Policy' },
];

class AdminPolicies extends Component {
  state = {
    type: 'partner_terms',
    versions: [],
    current: null,          // current in-force doc
    loading: true,
    // editor
    editorOpen: false,
    title: '',
    content: '',
    changeNote: '',
    effectiveDate: '',      // optional yyyy-mm-dd; blank = immediate
    publishing: false,
    confirming: false,
    // viewer
    viewing: null,          // full doc of a historical version
    previewMode: false,
  };

  componentDidMount() { this.load(); }

  load = async () => {
    this.setState({ loading: true, viewing: null });
    const { type } = this.state;
    try {
      const [versionsRes, currentRes] = await Promise.all([
        adminService.getPolicyVersions(type),
        adminService.getPolicyCurrent(type).catch(() => null),
      ]);
      this.setState({
        versions: versionsRes?.responseData?.versions || [],
        current: currentRes?.responseData || null,
        loading: false,
      });
    } catch (e) {
      this.setState({ loading: false });
      toast.error('Could not load policy versions');
    }
  };

  switchType = (type) => {
    this.setState({ type, editorOpen: false, confirming: false, viewing: null }, this.load);
  };

  openEditor = () => {
    // Pre-fill from the current version so publishing an amendment starts
    // from the in-force text rather than a blank page.
    const { current, type } = this.state;
    this.setState({
      editorOpen: true,
      confirming: false,
      previewMode: false,
      title: current?.title || POLICY_TYPES.find(t => t.key === type)?.label || '',
      content: current?.content || '',
      changeNote: '',
      effectiveDate: '',
    });
  };

  publish = async () => {
    const { type, title, content, changeNote, effectiveDate } = this.state;
    this.setState({ publishing: true });
    try {
      const payload = { title: title.trim(), content, changeNote: changeNote.trim() };
      if (effectiveDate) payload.effectiveDate = new Date(effectiveDate + 'T00:00:00').toISOString();
      const res = await adminService.publishPolicy(type, payload);
      const v = res?.responseData?.version;
      toast.success(`Published version ${v}`);
      this.setState({ publishing: false, editorOpen: false, confirming: false });
      this.load();
    } catch (e) {
      this.setState({ publishing: false, confirming: false });
      toast.error(e?.message || 'Publish failed');
    }
  };

  viewVersion = async (version) => {
    const { type } = this.state;
    try {
      const res = await adminService.getPolicyVersion(type, version);
      this.setState({ viewing: res?.responseData || null });
    } catch (e) {
      toast.error('Could not load that version');
    }
  };

  render() {
    const { type, versions, current, loading, editorOpen, title, content, changeNote,
      effectiveDate, publishing, confirming, viewing, previewMode } = this.state;
    const typeLabel = POLICY_TYPES.find(t => t.key === type)?.label;

    return (
      <AdminWrapper {...this.props}>
        <div className="admin-page-header">
          <h1>Policies</h1>
          <p>Versioned legal documents. Publishing creates a new dated version — previous versions are kept forever and shown to nobody but you.</p>
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
          {POLICY_TYPES.map(t => (
            <button key={t.key} type="button" onClick={() => this.switchType(t.key)}
              style={{
                padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                border: '1px solid', borderColor: type === t.key ? '#29282D' : '#e0e0e0',
                background: type === t.key ? '#29282D' : '#fff', color: type === t.key ? '#fff' : '#344054',
              }}>
              {t.label}
            </button>
          ))}
        </div>

        {loading ? <p style={{ color: '#888' }}>Loading…</p> : (
          <>
            {/* Current version card */}
            <div style={{ border: '1px solid #ececec', borderRadius: 12, background: '#fff', padding: '16px 20px', marginBottom: 16 }}>
              {current ? (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 15 }}>{current.title}</div>
                    <div style={{ fontSize: 13, color: '#667085', marginTop: 2 }}>
                      Version {current.version} in force — effective {new Date(current.effectiveDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button type="button" className="admin-btn" onClick={() => this.viewVersion(current.version)}>View</button>
                    <button type="button" className="admin-btn admin-btn-primary" onClick={this.openEditor}>New version</button>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: '#B54708', fontSize: 14 }}>
                    No version published yet — the public page is falling back to the legacy content.
                  </span>
                  <button type="button" className="admin-btn admin-btn-primary" onClick={this.openEditor}>Publish first version</button>
                </div>
              )}
            </div>

            {/* Editor */}
            {editorOpen && (
              <div style={{ border: '1px solid #ececec', borderRadius: 12, background: '#fff', padding: '16px 20px', marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <strong>New {typeLabel} version</strong>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button type="button" className="admin-btn" onClick={() => this.setState({ previewMode: !previewMode })}>
                      {previewMode ? 'Edit' : 'Preview'}
                    </button>
                    <button type="button" className="admin-btn" onClick={() => this.setState({ editorOpen: false, confirming: false })}>Close</button>
                  </div>
                </div>

                <input type="text" value={title} placeholder="Document title"
                  onChange={(e) => this.setState({ title: e.target.value })}
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #e0e0e0', borderRadius: 8, marginBottom: 10, fontSize: 14 }} />

                {previewMode ? (
                  <div style={{ border: '1px solid #f0f0f0', borderRadius: 8, padding: '16px 20px', maxHeight: 480, overflowY: 'auto', marginBottom: 10 }}>
                    <ReactMarkdown>{content}</ReactMarkdown>
                  </div>
                ) : (
                  <textarea value={content} placeholder="Content (Markdown)"
                    onChange={(e) => this.setState({ content: e.target.value })}
                    style={{ width: '100%', minHeight: 420, padding: '10px 12px', border: '1px solid #e0e0e0', borderRadius: 8, fontFamily: 'ui-monospace, monospace', fontSize: 13, lineHeight: 1.5, marginBottom: 10 }} />
                )}

                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
                  <input type="text" value={changeNote} placeholder="What changed? (shown in version history)"
                    onChange={(e) => this.setState({ changeNote: e.target.value })}
                    style={{ flex: 1, minWidth: 260, padding: '8px 12px', border: '1px solid #e0e0e0', borderRadius: 8, fontSize: 13 }} />
                  <label style={{ fontSize: 13, color: '#667085', display: 'flex', alignItems: 'center', gap: 6 }}>
                    Effective date
                    <input type="date" value={effectiveDate} onChange={(e) => this.setState({ effectiveDate: e.target.value })}
                      style={{ padding: '7px 10px', border: '1px solid #e0e0e0', borderRadius: 8, fontSize: 13 }} />
                    <span style={{ color: '#98A2B3' }}>(blank = immediately)</span>
                  </label>
                </div>

                {!confirming ? (
                  <button type="button" className="admin-btn admin-btn-primary"
                    disabled={!title.trim() || !content.trim()}
                    onClick={() => this.setState({ confirming: true })}>
                    Publish…
                  </button>
                ) : (
                  <div style={{ padding: '12px 14px', border: '1px solid #e0e0e0', borderRadius: 10, background: '#fafafa' }}>
                    <p style={{ margin: 0, fontSize: 13 }}>
                      This publishes <strong>{typeLabel} version {(versions[0]?.version || 0) + 1}</strong>
                      {effectiveDate ? <> effective <strong>{new Date(effectiveDate + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</strong></> : ' effective immediately'}.
                      It becomes the live document on the public pages and signup flows, and cannot be edited — only superseded.
                    </p>
                    <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                      <button type="button" className="admin-btn admin-btn-primary" onClick={this.publish} disabled={publishing}>
                        {publishing ? 'Publishing…' : 'Confirm publish'}
                      </button>
                      <button type="button" className="admin-btn" onClick={() => this.setState({ confirming: false })} disabled={publishing}>Cancel</button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Version viewer */}
            {viewing && (
              <div style={{ border: '1px solid #ececec', borderRadius: 12, background: '#fff', padding: '16px 20px', marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <div>
                    <strong>{viewing.title}</strong>
                    <span style={{ fontSize: 13, color: '#667085', marginLeft: 10 }}>
                      v{viewing.version} — effective {new Date(viewing.effectiveDate).toLocaleDateString('en-GB')}
                    </span>
                  </div>
                  <button type="button" className="admin-btn" onClick={() => this.setState({ viewing: null })}>Close</button>
                </div>
                <div style={{ border: '1px solid #f0f0f0', borderRadius: 8, padding: '16px 20px', maxHeight: 520, overflowY: 'auto' }}>
                  <ReactMarkdown>{viewing.content}</ReactMarkdown>
                </div>
              </div>
            )}

            {/* Version history */}
            <div style={{ border: '1px solid #ececec', borderRadius: 12, background: '#fff', padding: '16px 20px' }}>
              <strong style={{ display: 'block', marginBottom: 8 }}>Version history</strong>
              {versions.length === 0 ? <p style={{ color: '#888', fontSize: 13, margin: 0 }}>Nothing published yet.</p> : (
                <table className="admin-table" style={{ width: '100%', fontSize: 13 }}>
                  <thead><tr>
                    <th style={{ textAlign: 'left' }}>Version</th>
                    <th style={{ textAlign: 'left' }}>Published</th>
                    <th style={{ textAlign: 'left' }}>Effective</th>
                    <th style={{ textAlign: 'left' }}>Change note</th>
                    <th></th>
                  </tr></thead>
                  <tbody>
                    {versions.map(v => (
                      <tr key={v.version}>
                        <td>v{v.version}{current && v.version === current.version ? ' (current)' : ''}
                          {new Date(v.effectiveDate) > new Date() ? ' (scheduled)' : ''}</td>
                        <td>{new Date(v.publishedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                        <td>{new Date(v.effectiveDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                        <td style={{ color: '#667085' }}>{v.changeNote || '—'}</td>
                        <td><button type="button" className="admin-btn" onClick={() => this.viewVersion(v.version)}>View</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </AdminWrapper>
    );
  }
}

export default AdminPolicies;
